import { ArrowLeft } from 'lucide-react';
import { useSmartBack } from '../hooks/useSmartBack';

interface BackHeaderProps {
  /** Fallback route when there is no in-app history (deep links / PWA cold start). */
  fallback?: string;
  /**
   * Optional breadcrumb text for admin screens, e.g. "Administración › Empleados".
   * Rendered as: muted root, separator, primary label.
   */
  breadcrumb?: string;
  className?: string;
}

/**
 * Shared back affordance for screens with no browser-back UI (installed-PWA mode).
 * Uses useSmartBack: navigate(-1) when there is in-app history, otherwise the
 * given fallback (typically the current lens dashboard). Minimal by design —
 * drop it as the first child of a screen's existing header/container.
 */
export function BackHeader({ fallback = '/', breadcrumb, className = '' }: BackHeaderProps) {
  const goBack = useSmartBack(fallback);
  const crumb = breadcrumb ? breadcrumb.split('›').map((s) => s.trim()) : null;
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <button
        type="button"
        onClick={goBack}
        aria-label="Volver"
        title="Volver"
        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[var(--v-text-secondary)] transition-colors hover:bg-[var(--v-surface-2)] hover:text-[var(--v-text-primary)]"
      >
        <ArrowLeft className="h-5 w-5" aria-hidden="true" />
      </button>
      {crumb && (
        <nav aria-label="Breadcrumb" className="min-w-0 truncate text-sm">
          {crumb.map((part, i) => (
            <span key={i}>
              {i > 0 && <span className="mx-1 text-[var(--v-text-tertiary,#94a3b8)]">›</span>}
              <span className={i === crumb.length - 1 ? 'text-[var(--v-text-primary)] font-medium' : 'text-[var(--v-text-secondary)]'}>
                {part}
              </span>
            </span>
          ))}
        </nav>
      )}
    </div>
  );
}

export default BackHeader;
